import { type Input } from "antd";

import { resetAnalyticsIdentity } from "@btnet/utilities-analytics";

import { type AppDefaultInfo, type UserInfo } from "helpers/AppProvider.types";

import { BTLocalStorage } from "types/btStorage";
import { BTLoginTypes } from "types/enum";

import {
    getAssumedPortalTypeForUrl,
    getPortalForLoginType,
    isPortalAgnosticUrl,
} from "utilities/portal/portal";
import { routes } from "utilities/routes";
import { shutdownChat } from "utilities/thirdPartyIntegrations/serviceCloud/serviceCloud";
import { getOAuthUrl } from "utilities/url/url";

import {
    clearAllBuildersJobPickerKeys,
    clearJobPickerKeys,
} from "commonComponents/utilities/JobPicker/JobPicker.utils";

import {
    type ILoginResponse,
    type IRedirectPageParams,
    type OAuthType,
} from "entity/login/Login/Login.api.types";

export function getCanRedirectForExistingLogin(
    reason: string | undefined,
    oauthRedirectType: OAuthType | undefined,
    userInfo: UserInfo | undefined,
    promptLogin: boolean | undefined,
) {
    return (
        reason === undefined && // do not redirect when the user is presented with a reason
        !(oauthRedirectType && promptLogin) && // do not redirect during oauth flow where prompt=login
        userInfo !== undefined && // require a user
        userInfo != null &&
        userInfo.globalUserId !== null &&
        (userInfo.loginType === BTLoginTypes.BUILDER || // only these 5 login types are supported
            userInfo.loginType === BTLoginTypes.SUBS ||
            userInfo.loginType === BTLoginTypes.OWNER ||
            userInfo.loginType === BTLoginTypes.CONTACT)
    );
}

export function handleAutoFill<TValues>(
    usernameKey: keyof TValues,
    usernameValue: string,
    usernameRef: React.RefObject<Input>,
    setFieldValue: (key: keyof TValues, value: any) => void,
) {
    if (usernameRef.current && usernameRef.current.state.value !== usernameValue) {
        setFieldValue(usernameKey, usernameRef.current.state.value);
    }
}

export const getLastPageRequested = (loginType: BTLoginTypes, loginPostRedirect: string) => {
    const decodedPostLoginUrl = unescape(loginPostRedirect);
    let redirectUrl = "/".concat(decodedPostLoginUrl);
    redirectUrl = redirectUrl.replace("//", "/");

    if (
        !isPortalAgnosticUrl(loginPostRedirect) &&
        getAssumedPortalTypeForUrl(loginPostRedirect) !== getPortalForLoginType(loginType)
    ) {
        return routes.landingPage.getLinkForLoginType(loginType, true);
    }

    return redirectUrl;
};

export const handleRedirectPage = (params: IRedirectPageParams) => {
    const response = params.loginResponse;

    // Clears localStorage of previous Job Picker keys
    if (response.builderId) {
        clearJobPickerKeys(response.builderId);
    }

    if (params.isOAuthRequest && response.oAuthCode) {
        handleOAuthRedirect(params, response);
    }
};

export const handleLogout = (appDefaults?: AppDefaultInfo) => {
    // Drop the pending v3 queue and correlation id before the next user signs
    // in. On a shared workstation the queue outlives the session, so events
    // below the batch threshold would otherwise post under the next user's
    // identity.
    resetAnalyticsIdentity();
    BTLocalStorage.remove("bt-object-previousAuthStoreInfo");
    BTLocalStorage.remove("bt-number-selectedBuilderId");
    BTLocalStorage.remove("bt-object-selectedJobsiteIdWithBuilder");
    clearAllBuildersJobPickerKeys();
    if (appDefaults?.enableServiceCloudChat) {
        BTLocalStorage.removeServiceCloudStorageItems(
            appDefaults?.serviceCloudConfig.salesforceOrgId,
        );
        shutdownChat();
    }
    const logoutUrl = routes.logout.getLogoutLink();
    window.location.assign(logoutUrl);
};

const handleOAuthRedirect = (params: IRedirectPageParams, response: ILoginResponse) => {
    const oauthRedirectState = BTLocalStorage.get("bt-object-oauthRedirectState");
    BTLocalStorage.remove("bt-object-oauthRedirectState");
    const redirectURI = params.oauthRedirectUri ?? oauthRedirectState?.redirectURI;
    const stateParam = params.oauthState ?? oauthRedirectState?.state;
    if (redirectURI && stateParam && response.oAuthCode) {
        window.location.assign(getOAuthUrl(redirectURI, response.oAuthCode, stateParam));
    }
};
